// ============================================
// Creator Editor - Situational Images
// 상황별 이미지 관리 모듈
// ============================================

import { showToast, escapeHtml } from '../../utils.js';
import { showConfirm } from '../../shared/confirm-modal.js';
import {
  getSituationalImages,
  getEditingSituationalIndex,
  setSituationalImages,
  addSituationalImage,
  updateSituationalImage,
  removeSituationalImage,
  setEditingSituationalIndex,
  MAX_SITUATIONAL_IMAGES
} from '../state/index.js';
import { updatePublishButtonState } from './events.js';

// 현재 모달에서 선택한 이미지 데이터
let currentSituationalImageData = { url: null, file: null };

// 상황별 이미지 모드 플래그 (이미지 선택 시 구분용)
let isSituationalImageMode = false;

// ============================================
// Getters for external access
// ============================================

export function getIsSituationalImageMode() {
  return isSituationalImageMode;
}

export function setIsSituationalImageMode(mode) {
  isSituationalImageMode = mode;
}

// ============================================
// 아코디언 토글
// ============================================

export function toggleSituationalSection() {
  const section = document.getElementById('situationalSection');
  const content = document.getElementById('situationalSectionContent');
  if (!section || !content) return;

  section.classList.toggle('expanded');
  content.classList.toggle('expanded');
  content.classList.toggle('collapsed');
}

// ============================================
// 리스트 렌더링
// ============================================

export function renderSituationalImageList() {
  const listEl = document.getElementById('situationalImageList');
  const countEl = document.getElementById('situationalImageCount');
  if (!listEl) return;

  const images = getSituationalImages();

  if (countEl) {
    countEl.textContent = images.length;
  }

  if (images.length === 0) {
    listEl.innerHTML = '';
    return;
  }

  listEl.innerHTML = images.map((img, index) => {
    const imageUrl = img.imageUrl || img.url || '';
    const description = img.description || '';

    return `
      <div class="advanced-list-item" data-index="${index}" draggable="true">
        <div class="advanced-list-item-drag">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
            <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
            <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
          </svg>
        </div>
        <span class="advanced-list-item-number">${index + 1}</span>
        <div class="advanced-list-item-thumb">
          <img src="${imageUrl}" alt="" onerror="this.style.display='none'">
        </div>
        <div class="advanced-list-item-content" data-action="editSituational" data-index="${index}">
          <div class="advanced-list-item-desc">${escapeHtml(description)}</div>
        </div>
        <div class="advanced-list-item-actions">
          <button class="advanced-delete-btn" data-action="deleteSituational" data-index="${index}">삭제</button>
        </div>
      </div>
    `;
  }).join('');

  initSituationalDragAndDrop(listEl);
}

// 드래그 앤 드롭 초기화
function initSituationalDragAndDrop(listEl) {
  let draggedItem = null;

  listEl.querySelectorAll('.advanced-list-item').forEach(item => {
    item.addEventListener('dragstart', (e) => {
      draggedItem = item;
      item.classList.add('dragging');
      e.dataTransfer.effectAllowed = 'move';
    });

    item.addEventListener('dragend', () => {
      item.classList.remove('dragging');

      // 새 순서 저장
      const items = [...listEl.querySelectorAll('.advanced-list-item')];
      const newOrder = items.map(el => parseInt(el.dataset.index));

      // 순서가 변경되었는지 확인
      const isChanged = newOrder.some((idx, i) => idx !== i);
      if (isChanged) {
        reorderSituationalImages(newOrder);
        renderSituationalImageList();
      }

      draggedItem = null;
    });

    item.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (!draggedItem || draggedItem === item) return;

      const rect = item.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;

      if (e.clientY < midY) {
        item.parentNode.insertBefore(draggedItem, item);
      } else {
        item.parentNode.insertBefore(draggedItem, item.nextSibling);
      }
    });
  });
}

// 상황별 이미지 순서 변경
function reorderSituationalImages(newOrder) {
  const images = getSituationalImages();
  const reordered = newOrder.map(idx => images[idx]);
  setSituationalImages(reordered);
}

// ============================================
// 모달 관리
// ============================================

export function openSituationalImageModal(editIndex = null) {
  const images = getSituationalImages();

  // 새로 추가할 때 최대 개수 체크
  if (editIndex === null && images.length >= MAX_SITUATIONAL_IMAGES) {
    showToast(`상황별 이미지는 최대 ${MAX_SITUATIONAL_IMAGES}개까지 추가할 수 있어요`);
    return;
  }

  setEditingSituationalIndex(editIndex);

  const modal = document.getElementById('situationalImageModal');
  const previewEl = document.getElementById('situationalImagePreview');
  const descInput = document.getElementById('situationalDescription');
  const titleEl = document.getElementById('situationalModalTitle');
  const countEl = document.getElementById('situationalDescCount');

  if (editIndex !== null && images[editIndex]) {
    // 편집 모드
    const img = images[editIndex];
    const imageUrl = img.imageUrl || img.url || '';
    currentSituationalImageData = { url: imageUrl, file: img.file || null };

    if (imageUrl) {
      previewEl.innerHTML = `<img src="${imageUrl}" alt="선택된 이미지">`;
    } else {
      previewEl.innerHTML = getPlaceholderHTML();
    }

    descInput.value = img.description || '';
    titleEl.textContent = `상황별 이미지 ${editIndex + 1}`;
  } else {
    // 새로 추가 모드
    currentSituationalImageData = { url: null, file: null };
    previewEl.innerHTML = getPlaceholderHTML();
    descInput.value = '';
    titleEl.textContent = '상황별 이미지 추가';
  }

  // 글자 수 카운터 업데이트
  if (countEl) {
    countEl.textContent = descInput.value.length;
  }

  // 모달 표시
  modal?.offsetHeight; // reflow
  modal?.classList.add('active');

  if (window.ModalManager) {
    window.ModalManager.push('situationalImageModal');
  }
}

export function closeSituationalImageModal() {
  const modal = document.getElementById('situationalImageModal');
  modal?.classList.remove('active');

  if (window.ModalManager) {
    window.ModalManager.remove('situationalImageModal', true);
  }

  setEditingSituationalIndex(null);
  currentSituationalImageData = { url: null, file: null };
}

export function saveSituationalImage() {
  const descInput = document.getElementById('situationalDescription');
  const description = descInput?.value?.trim();

  if (!currentSituationalImageData.url) {
    showToast('이미지를 선택해 주세요');
    return;
  }

  if (!description) {
    showToast('상황 설명을 입력해 주세요');
    return;
  }

  const imageData = {
    imageUrl: currentSituationalImageData.url,
    file: currentSituationalImageData.file,
    description
  };

  const editIndex = getEditingSituationalIndex();
  if (editIndex !== null) {
    updateSituationalImage(editIndex, imageData);
    showToast('상황별 이미지가 수정됐어요');
  } else {
    addSituationalImage(imageData);
    showToast('상황별 이미지가 추가됐어요');
  }

  renderSituationalImageList();
  closeSituationalImageModal();
  updatePublishButtonState();
}

export async function deleteSituationalImage(index) {
  const confirmed = await showConfirm('이 상황별 이미지를 삭제할까요?');
  if (!confirmed) return;

  removeSituationalImage(index);
  renderSituationalImageList();
  showToast('상황별 이미지가 삭제됐어요');
  updatePublishButtonState();
}

// ============================================
// 이미지 선택
// ============================================

export function openSituationalImageSelector() {
  // 기존 에디터 이미지 액션 시트를 재사용
  isSituationalImageMode = true;
  const sheet = document.getElementById('editorImageActionSheet');
  sheet?.classList.add('open');
}

export function setSituationalImage(url, file = null) {
  currentSituationalImageData = { url, file };
  const previewEl = document.getElementById('situationalImagePreview');
  if (previewEl && url) {
    previewEl.innerHTML = `<img src="${url}" alt="선택된 이미지">`;
  }
}

export function resetSituationalImageMode() {
  isSituationalImageMode = false;
}

// ============================================
// 헬퍼 함수
// ============================================

function getPlaceholderHTML() {
  return `
    <div class="situational-image-placeholder">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <path d="M21 15l-5-5L5 21"/>
      </svg>
      <span>이미지 선택</span>
    </div>
  `;
}

// ============================================
// 초기화
// ============================================

export function initSituationalDescCounter() {
  const descInput = document.getElementById('situationalDescription');
  const countEl = document.getElementById('situationalDescCount');

  if (descInput && countEl) {
    descInput.addEventListener('input', () => {
      countEl.textContent = descInput.value.length;
    });
  }
}
